/**
 * "How to play" (docs/ux.md §15a.1): one short page per topic, in phase order, each with a
 * picture drawn by the board. The same guide serves the sheet in the game and the public page;
 * the game passes its own ruleset, so a game with changed options reads its own numbers.
 */
import { getPlayerColour } from '@brig/nations';
import { DEFAULT_RULESET, type Ruleset } from '@brig/rules-engine';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createBoardCamera } from '../board/boardCamera.ts';
import { BoardView } from '../board/BoardView.tsx';
import { headingTowardEnemy, type Heading } from '../ships/sides.ts';
import * as css from './HowToPlay.css.ts';
import { picture } from './pictures.ts';
import { TOPIC_RULES, TOPICS, topicNumbers, type Topic } from './topics.ts';

export function HowToPlay({
  topic,
  onTopic,
  ruleset = DEFAULT_RULESET,
}: {
  topic: Topic;
  onTopic: (topic: Topic) => void;
  ruleset?: Ruleset;
}) {
  const { t } = useTranslation();
  const numbers = useMemo(() => topicNumbers(ruleset), [ruleset]);
  const at = TOPICS.indexOf(topic);
  const prev = TOPICS[at - 1];
  const next = TOPICS[at + 1];
  const title = t(`learn.topics.${topic}.title`);
  return (
    <div className={css.guide} data-testid="how-to-play">
      <nav aria-label={t('learn.contents')}>
        <ul className={css.contents}>
          {TOPICS.map((id) => (
            <li key={id}>
              <button
                type="button"
                className={css.chip}
                aria-current={id === topic ? 'page' : undefined}
                onClick={() => {
                  onTopic(id);
                }}
              >
                {t(`learn.topics.${id}.title`)}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <article className={css.guide} aria-labelledby="learn-topic-title">
        <h3 id="learn-topic-title" className={css.title}>
          {title}
        </h3>
        {/* Remounted per topic: each picture is a board of its own. */}
        <LessonBoard
          key={topic}
          topic={topic}
          ruleset={ruleset}
          label={t('learn.picture', { topic: title })}
        />
        <div className={css.body}>
          {t(`learn.topics.${topic}.body`, numbers)
            .split('\n\n')
            .map((para) => (
              <p key={para}>{para}</p>
            ))}
        </div>
        <p className={css.rules}>{t('learn.rules', { ids: TOPIC_RULES[topic].join(', ') })}</p>
      </article>
      <div className={css.pager}>
        {prev ? (
          <button
            type="button"
            className={css.pagerButton}
            onClick={() => {
              onTopic(prev);
            }}
          >
            ← {t(`learn.topics.${prev}.title`)}
          </button>
        ) : (
          <span />
        )}
        {next ? (
          <button
            type="button"
            className={css.pagerButton}
            onClick={() => {
              onTopic(next);
            }}
          >
            {t(`learn.topics.${next}.title`)} →
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** The guide with its own page state, for a sheet. */
export function HowToPlaySheet({ ruleset, start = 'goal' }: { ruleset?: Ruleset; start?: Topic }) {
  const [topic, setTopic] = useState<Topic>(start);
  return <HowToPlay topic={topic} onTopic={setTopic} {...(ruleset ? { ruleset } : {})} />;
}

const none: ReadonlySet<string> = new Set();
const ignore = () => undefined;

function LessonBoard({ topic, ruleset, label }: { topic: Topic; ruleset: Ruleset; label: string }) {
  const [camera] = useState(createBoardCamera);
  const p = useMemo(() => picture(topic, ruleset), [topic, ruleset]);
  const state = p.history.states[0];
  const facing = useMemo(() => {
    const out = new Map<string, Heading>();
    if (!state) return out;
    const toward = headingTowardEnemy(state.ships);
    for (const s of state.ships) out.set(s.id, toward[s.side]);
    return out;
  }, [state]);
  if (!state) return null;
  const set = (ids: readonly string[] | undefined) => (ids ? new Set(ids) : none);
  return (
    <div className={css.picture} role="img" aria-label={label} inert>
      <BoardView
        state={state}
        camera={camera}
        projection="iso"
        mapStyle="chart"
        selected={p.selected ?? null}
        draftGroup={set(p.draftGroup)}
        moveCells={set(p.moveCells)}
        blockedCells={set(p.blockedCells)}
        portCells={none}
        targets={set(p.targets)}
        volleyTarget={p.volleyTarget ?? null}
        movable={set(p.movable)}
        volleyShips={set(p.volleyShips)}
        highlight={set(p.highlight)}
        facing={facing}
        nations={p.history.nations}
        colours={{
          red: getPlayerColour(p.history.colours.red).hex,
          blue: getPlayerColour(p.history.colours.blue).hex,
        }}
        minimap={false}
        onCell={ignore}
        onUnit={ignore}
      />
    </div>
  );
}
